import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import logo from "../assets/wamglammz-logo.png";
import logo2 from "../assets/wamglammz-logo2.png";
import { vendorArtistApi, saveAuthSession, getAuthToken } from "../lib/api";

const benefits = [
  "Publish a personalized booking link",
  "Manage services, pricing, and portfolio",
  "Track bookings, clients, notes, and invoices",
  "Start with a 1-month Stripe trial",
];

function BusinessAuthHeader() {
  return (
    <header className="border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex min-w-0 items-center gap-2">
          <img src={logo} alt="WamGlammz" className="h-9 w-auto object-contain" />
          <img src={logo2} alt="WamGlammz" className="h-6 w-auto object-contain" />
        </Link>

        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/"
            className="hidden h-10 items-center rounded-lg border border-slate-200 px-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 md:flex"
          >
            Back to website
          </Link>
        </div>
      </div>
    </header>
  );
}

const getFriendlyErrorMessage = (errorMsg) => {
  if (!errorMsg) return "An unexpected error occurred. Please try again.";
  const lower = errorMsg.toLowerCase();
  if (lower.includes("failed to fetch") || lower.includes("networkerror") || lower.includes("inbound response pipeline")) {
    return "We are unable to reach our services. Please check your internet connection or try again shortly.";
  }
  if (lower.includes("unexpected token") || lower.includes("json") || lower.includes("parsing")) {
    return "The server returned an unreadable response. This might be a temporary issue; please try again shortly.";
  }
  return errorMsg;
};

export default function BusinessAccess() {
  const navigate = useNavigate();

  useEffect(() => {
    if (getAuthToken()) {
      navigate("/for-business/dashboard", { replace: true });
    }
  }, [navigate]);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  const handleSendOtp = async (event) => {
    event.preventDefault();
    setFeedback("");
    setInfoMessage("");
    if (!email) {
      setFeedback("Email address is required");
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await vendorArtistApi.getOtp(email);
      if (response.success) {
        setOtpSent(true);
        setInfoMessage(`OTP sent successfully! ${response.otp ? `(Debug OTP: ${response.otp})` : ""}`);
      } else {
        setFeedback(getFriendlyErrorMessage(response.message || "Failed to send OTP"));
      }
    } catch (error) {
      setFeedback(getFriendlyErrorMessage(error.message || "Unable to send OTP. Please check the email address."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    setFeedback("");
    setInfoMessage("");
    if (!otp) {
      setFeedback("OTP is required");
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await vendorArtistApi.verifyOtp(email, otp);
      if (response.success && response.data) {
        saveAuthSession(response.data);
        navigate("/for-business/dashboard");
      } else {
        setFeedback(getFriendlyErrorMessage(response.message || "Invalid OTP"));
      }
    } catch (error) {
      setFeedback(getFriendlyErrorMessage(error.message || "OTP verification failed. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F9FF] font-sans text-slate-800">
      <BusinessAuthHeader />

      <main className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8 lg:py-14">
        <section className="rounded-2xl border border-blue-100 bg-white p-6 shadow-[0_16px_45px_rgba(14,63,182,0.07)] lg:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">For Business</p>
          <h1 className="mt-3 text-3xl font-extrabold leading-tight text-[#191E49] sm:text-4xl">
            Run your artist bookings from one Wamglammz dashboard.
          </h1>
          <p className="mt-4 text-base leading-7 text-slate-600">
            Vendors can register, set up services, publish a booking link, and manage schedules from a centralized workspace.
          </p>

          <div className="mt-8 space-y-3">
            {benefits.map((benefit) => (
              <div key={benefit} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm font-semibold text-slate-700">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-blue-50 text-blue-700">
                  <i className="fa-solid fa-check text-[11px]" aria-hidden="true" />
                </span>
                {benefit}
              </div>
            ))}
          </div>


        </section>

        <section className="rounded-2xl border border-blue-100 bg-white p-6 shadow-[0_16px_45px_rgba(14,63,182,0.07)] lg:p-8">
          <div className="flex items-center gap-3">
            <img src={logo} alt="WamGlammz" className="h-12 w-auto object-contain" />
            <img src={logo2} alt="WamGlammz" className="h-7 w-auto object-contain" />
          </div>

          <div className="mt-7">
            <h2 className="text-2xl font-extrabold text-[#191E49]">
              Vendor Access Portal
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Enter your email address to receive a one-time password (OTP) and access your dashboard.
            </p>
          </div>

          {!otpSent ? (
            <form className="mt-7 space-y-4" onSubmit={handleSendOtp}>
              <label className="block">
                <span className="text-sm font-bold text-slate-700">Email Address</span>
                <input
                  type="email"
                  placeholder="vendor@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm font-semibold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              </label>

              {feedback && (
                <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                  {feedback}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="flex h-12 w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:shadow-blue-200 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? "Sending..." : "Send OTP"}
              </button>
            </form>
          ) : (
            <form className="mt-7 space-y-4" onSubmit={handleVerifyOtp}>
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm font-semibold text-blue-800">
                OTP sent to <span className="font-bold">{email}</span>
              </div>

              <label className="block">
                <span className="text-sm font-bold text-slate-700">Verification Code (OTP)</span>
                <input
                  type="text"
                  placeholder="123456"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm font-semibold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              </label>

              {infoMessage && (
                <div className="rounded-xl border border-green-100 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
                  {infoMessage}
                </div>
              )}

              {feedback && (
                <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                  {feedback}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="flex h-12 w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#0A3FB6] to-[#2FB4FF] text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:shadow-blue-200 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? "Verifying..." : "Verify OTP & Continue"}
              </button>

              <button
                type="button"
                onClick={() => setOtpSent(false)}
                className="w-full text-center text-sm font-bold text-blue-700 hover:underline mt-2"
              >
                Change Email Address
              </button>
            </form>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
